import { Controller } from 'stimulus';

export default class SiteMenuController extends Controller {
  static targets = ['menu', 'link', 'submenu'];

  opened = false;

  openingTimer = null;

  openingTimerValue = 200;

  currentSubmenu = null;

  lastScrollPosition = window.pageYOffset;

  extendedHoverAreas = [];

  pendingTurbolinksTransition = false;

  isTransitionedWithTurbolinks = false;

  isWatchingScroll = true;

  connect() {
    if (this.element.dataset.spa) {
      this.isTransitionedWithTurbolinks = true;
    }
    this.submenuTargets.forEach((submenu) => {
      submenu.addEventListener('transitionend', this.restoreHeightAfterTransition);
      submenu.classList.remove('opened');
    });

    if (this.menuTarget) {
      for (let i = 0; i < this.menuTarget.children.length; i++) {
        const item = this.menuTarget.children[i];
        item.addEventListener('mouseenter', this.toggleSubmenu);
        item.addEventListener('mouseleave', this.toggleSubmenu);
      }
    }

    const area = document.createElement('div');
    area.classList.add('extended-hover');

    this.linkTargets.forEach((link) => {
      const element = area.cloneNode();
      link.appendChild(element);
      this.extendedHoverAreas.push(element);
    });
    window.addEventListener('scroll', this.onScroll);
    window.addEventListener('bindScroll', this.onBindScroll);
    window.addEventListener('unbindScroll', this.onUnbindScroll);
    document.addEventListener('turbolinks:click', this.onTurbolinksClick);
    document.addEventListener('turbolinks:before-render', this.onTurbolinksBeforeRender);
  }

  disconnect() {
    this.submenuTargets.forEach((submenu) => (
      submenu.removeEventListener('transitionend', this.restoreHeightAfterTransition)
    ));
    if (this.menuTarget) {
      for (let i = 0; i < this.menuTarget.children.length; i++) {
        const item = this.menuTarget.children[i];
        item.removeEventListener('mouseenter', this.toggleSubmenu);
        item.removeEventListener('mouseleave', this.toggleSubmenu);
      }
    }

    this.linkTargets.forEach((link, i) => (
      link.removeChild(this.extendedHoverAreas[i])
    ));
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('bindScroll', this.onBindScroll);
    window.removeEventListener('unbindScroll', this.onUnbindScroll);
    document.removeEventListener('turbolinks:click', this.onTurbolinksClick);
    document.removeEventListener('turbolinks:before-render', this.onTurbolinksBeforeRender);
  }

  findSubmenu(element) {
    return element.querySelector('[data-site-menu-target="submenu"]');
  }

  onScroll = () => {
    if (!this.isWatchingScroll) {
      return;
    }

    const currentScrollPosition = window.pageYOffset;
    const scrollingUp = this.lastScrollPosition > currentScrollPosition;
    this.lastScrollPosition = currentScrollPosition;

    if (!scrollingUp && this.opened && this.currentSubmenu) {
      this.closeSubmenu(this.currentSubmenu);
    }
  };

  restoreHeightAfterTransition = (event) => {
    if (event.propertyName === 'height') {
      event.target.style.height = '';
    }
  };

  openSubmenu(submenu) {
    window.clearTimeout(this.openingTimer);
    this.openingTimer = window.setTimeout(() => {
      if (!this.isTransitionedWithTurbolinks) {
        this.element.classList.add('opened');
        submenu.classList.add('opened');
      }
      this.opened = true;
      this.currentSubmenu = submenu;
    }, this.openingTimerValue);
  }

  swapSubmenu(prevSubmenu, nextSubmenu) {
    const currentHeight = this.isTransitionedWithTurbolinks ? 0 : prevSubmenu.offsetHeight;
    const futureHeight = nextSubmenu.offsetHeight;
    prevSubmenu.classList.add('no-transition');
    prevSubmenu.classList.remove('opened');
    nextSubmenu.style.height = `${currentHeight}px`;
    nextSubmenu.classList.add('no-transition', 'opened');
    /* eslint-disable no-unused-expressions */
    nextSubmenu.offsetWidth;
    const oldSubmenu = prevSubmenu;
    this.currentSubmenu = nextSubmenu;
    window.requestAnimationFrame(() => {
      oldSubmenu.classList.remove('no-transition');
      nextSubmenu.classList.remove('no-transition');
      nextSubmenu.style.height = futureHeight === currentHeight ? '' : `${futureHeight}px`;
      this.isTransitionedWithTurbolinks = false;
    });
  }

  closeSubmenu(submenu) {
    if (!submenu) {
      return;
    }

    window.clearTimeout(this.openingTimer);
    this.element.classList.remove('opened');
    submenu.classList.remove('opened');
    this.opened = false;
    this.isTransitionedWithTurbolinks = false;
  }

  toggleSubmenu = (event) => {
    const submenu = this.findSubmenu(event.target);
    if (!submenu) {
      return;
    }

    if (event.type === 'mouseenter') {
      if (!this.opened) {
        this.openSubmenu(submenu);
      } else {
        this.swapSubmenu(this.currentSubmenu, submenu);
      }
    } else {
      let element = event.relatedTarget;
      let isBeyondMenu = true;
      let currentItemHasSubmenu = false;
      while (element) {
        if (element.tagName === 'LI' && this.findSubmenu(element)) {
          currentItemHasSubmenu = true;
        }
        if (element === this.menuTarget) {
          isBeyondMenu = false;
          break;
        }

        element = element.parentElement;
      }
      if (isBeyondMenu || !currentItemHasSubmenu) {
        this.closeSubmenu(this.currentSubmenu);
      }
    }
  };

  onTurbolinksClick = (event) => {
    this.pendingTurbolinksTransition = this.linkTargets.indexOf(event.target) >= 0;
  };

  onTurbolinksBeforeRender = (event) => {
    if (!this.pendingTurbolinksTransition) {
      return;
    }

    this.pendingTurbolinksTransition = false;
    const newMenu = event.data.newBody.querySelector(
      `[data-controller="${this.element.dataset.controller}"]`,
    );
    newMenu.dataset.spa = true;
  };

  onBindScroll = () => { this.isWatchingScroll = true; };

  onUnbindScroll = () => { this.isWatchingScroll = false; };
}
